import moment from 'moment';

/**
 * Calculates a new start date when resuming a paused subscription.
 * @param {number} daysUsed - The number of redeemable days already consumed.
 * @returns {string} - The new start date in 'YYYY-MM-DD' format.
 */
export const getNewStartDateOnResume = (daysUsed) => {
  let newStartDate = moment();
  let daysToSubtract = daysUsed;

  while (daysToSubtract > 0) {
    newStartDate.subtract(1, 'day');
    if (newStartDate.day() !== 0) {
      daysToSubtract--;
    }
  }
  return newStartDate.format('YYYY-MM-DD');
};

/**
 * The core logic engine that determines the current state of a subscription.
 * @param {object} subscription - The subscription object from Supabase.
 * @param {Date} currentDate - The current date.
 * @returns {object} - The calculated state of the subscription.
 */
export const calculateSubscriptionState = (subscription, currentDate = new Date()) => {
  if (!subscription) {
    return { status: 'invalid' };
  }
  if (subscription.status === 'canceled') {
    return { status: 'canceled', isExpired: true, currentWeek: 0, currentDayInWeek: 0, isSunday: false };
  }

  if (subscription.status === 'paused') {
    const daysUsed = subscription.redeemable_days_used || 0;
    const currentWeek = daysUsed > 0 ? Math.floor((daysUsed - 1) / 6) + 1 : 1;
    const currentDayInWeek = daysUsed > 0 ? ((daysUsed - 1) % 6) + 1 : 0;
    return {
      status: 'paused',
      currentWeek: currentWeek,
      currentDayInWeek: currentDayInWeek,
      redeemableDaysPassed: daysUsed,
      isSunday: false,
      isExpired: false,
    };
  }
  
  if (subscription.status !== 'active' || !subscription.start) {
    return {
      status: subscription.status,
      currentWeek: 0, 
      currentDayInWeek: 0, 
      isSunday: moment(currentDate).day() === 0, 
      isExpired: false,
    };
  }

  const today = moment(currentDate).startOf('day');
  const startDate = moment(subscription.start).startOf('day');

  let redeemableDaysPassed = 0;
  let tempDate = startDate.clone();
  while (tempDate.isSameOrBefore(today)) {
    if (tempDate.day() !== 0) {
      redeemableDaysPassed++;
    }
    tempDate.add(1, 'day');
  }

  const totalWeeks = subscription.benefits?.weeks?.length || 4;
  const totalRedeemableDays = totalWeeks * 6;
  const isExpired = redeemableDaysPassed > totalRedeemableDays;

  if (isExpired) {
    return { status: 'expired', currentWeek: totalWeeks + 1, currentDayInWeek: 0, isSunday: false, isExpired: true };
  }

  const currentWeek = redeemableDaysPassed > 0 ? Math.floor((redeemableDaysPassed - 1) / 6) + 1 : 1;
  const currentDayInWeek = redeemableDaysPassed > 0 ? ((redeemableDaysPassed - 1) % 6) + 1 : 0;

  return {
    status: 'active',
    currentWeek,
    currentDayInWeek,
    isSunday: today.day() === 0,
    isExpired: false,
    redeemableDaysPassed,
  };
};

/**
 * Distributes SINGLE MONTH benefits into a 4-week structure.
 * @param {number} monthlyDrinks - Total drinks for ONE month.
 * @param {number} monthlyBreakfasts - Total breakfasts for ONE month.
 * @returns {Array} - An array of 4 week objects with distributed benefits.
 */
export const distributeBenefitsToWeeks = (monthlyDrinks = 0, monthlyBreakfasts = 0) => {
    const weeksInMonth = 4;
    const weeks = Array(weeksInMonth).fill(null).map((_, i) => ({
        week: i + 1, // This week number is a placeholder, it gets re-numbered later
        drinks: { total: Math.floor(monthlyDrinks / weeksInMonth), redeemed: 0 },
        breakfasts: { total: Math.floor(monthlyBreakfasts / weeksInMonth), redeemed: 0 },
    }));

    let remainingDrinks = monthlyDrinks % weeksInMonth;
    let remainingBreakfasts = monthlyBreakfasts % weeksInMonth;

    for (let i = 0; i < weeksInMonth; i++) {
        if (remainingDrinks > 0) {
            weeks[i].drinks.total++;
            remainingDrinks--;
        }
        if (remainingBreakfasts > 0) {
            weeks[i].breakfasts.total++;
            remainingBreakfasts--;
        }
    }

    return weeks;
};